import { useEffect, useState } from 'react';
import { ArrowRight, History, RefreshCw, RotateCcw, ShieldCheck } from 'lucide-react';
import type { UpdaterInstallationRecord } from '../../types/modpackUpdater';
import { modpackUpdaterApi } from '../../services/modpackUpdaterApi';
import { SectionHeader } from '../ui/SectionHeader';

export function PackInstallationHistory({ history, onOpenJob }: {
  history?: UpdaterInstallationRecord[] | null;
  onOpenJob?: (jobId: string) => void;
}) {
  return <section className="subcard p-4 space-y-3" aria-label="Accepted installation history">
    <SectionHeader title="Installation history" icon={History} />
    {history === null ? <p className="text-sm text-[var(--text-muted)]">Loading installation history…</p>
      : !history ? <p className="text-sm text-[var(--text-muted)]">History unavailable.</p>
        : history.length === 0 ? <p className="text-sm text-[var(--text-muted)]">No updates or rollbacks yet.</p>
          : <ol className="space-y-3">{history.map(record => <li key={record.id} className="rounded-lg border border-glass-200/10 p-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="inline-flex items-center gap-2 font-medium text-[var(--text-primary)]">
                {record.action === 'updater_installation_rolled_back' ? <RotateCcw className="w-4 h-4 text-amber-300" /> : <ShieldCheck className="w-4 h-4 text-emerald-300" />}
                {record.action === 'updater_installation_rolled_back' ? 'Rollback accepted' : 'Update accepted'}
              </span>
              <time className="text-[var(--text-muted)]" dateTime={new Date(record.timestamp).toISOString()}>{new Date(record.timestamp).toLocaleString()}</time>
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center text-sm">
              <div className="min-w-0"><p className="text-xs text-[var(--text-muted)]">Before</p><p className="break-all text-[var(--text-primary)]">{record.details.before.modpack_version || 'Not recorded'}</p><p className="text-xs break-all text-[var(--text-muted)]">{record.details.before.modloader || 'Loader not recorded'}</p></div>
              <ArrowRight className="w-4 h-4 text-[var(--text-muted)]" aria-hidden="true" />
              <div className="min-w-0"><p className="text-xs text-[var(--text-muted)]">After acceptance</p><p className="break-all text-[var(--text-primary)]">{record.details.after.modpack_version || 'Not recorded'}</p><p className="text-xs break-all text-[var(--text-muted)]">{record.details.after.modloader || 'Loader not recorded'}</p></div>
            </div>
            <p className="text-xs text-[var(--text-secondary)] break-all">Accepted by <strong>{record.actor_id}</strong> · {record.details.machine_id} / {record.details.amp_name}</p>
            {record.action === 'updater_installation_committed' && <p className="text-xs text-[var(--text-secondary)]" aria-label="Discord announcement status">
              {record.details.announcement ? announcementStatus(record.details.announcement) : 'Discord announcement: no delivery record for this older update.'}
            </p>}
            <details className="text-xs text-[var(--text-muted)]">
              <summary className="cursor-pointer">Transaction and audit reference</summary>
              <dl className="mt-2 space-y-1 break-all">
                <div><dt className="inline">Transaction: </dt><dd className="inline font-mono">{record.details.transaction_id}</dd></div>
                <div><dt className="inline">Operation: </dt><dd className="inline font-mono">{record.details.operation_id}</dd></div>
                <div><dt className="inline">Audit: </dt><dd className="inline font-mono">{record.id}</dd></div>
              </dl>
            </details>
            {record.details.job_id && onOpenJob && <button type="button" className="glass-button-sm" onClick={() => onOpenJob(record.details.job_id!)}>Open this updater run</button>}
          </li>)}</ol>}
  </section>;
}

function announcementStatus(announcement: NonNullable<UpdaterInstallationRecord['details']['announcement']>) {
  if (announcement.state === 'sent') return `Discord: announced${announcement.sent_at ? ` · ${new Date(announcement.sent_at * 1000).toLocaleString()}` : ''}`;
  if (announcement.message_id || announcement.sent_at) {
    if (announcement.state === 'cancelled') return 'Discord: announced earlier · remaining notes or credit cancelled';
    return announcement.reason === 'announcement_requires_reconciliation'
      ? 'Discord: announced · notes or credit need checking'
      : 'Discord: announced · notes or credit still pending';
  }
  const reasons: Record<string, string> = {
    update_not_finished: 'waiting for this update to finish',
    cluster_not_fully_updated: 'waiting for every cluster member to reach the target version',
    cluster_work_incomplete: 'waiting for the remaining cluster updates or recovery',
    cluster_still_in_maintenance: 'waiting for cluster maintenance to end',
    announcement_delivery_pending: 'delivery queued',
    announcement_requires_reconciliation: 'delivery needs checking before retrying',
    announcement_service_unavailable: 'announcement service unavailable; retry scheduled',
    announcement_side_effects_pending: 'announcement posted; notes or credit still pending',
    installation_superseded: 'a later installation replaced this update',
    installation_rolled_back: 'this update was rolled back',
  };
  const state = { pending: 'pending', sending: 'sending', blocked: 'needs attention', cancelled: 'cancelled' }[announcement.state];
  return `Discord: ${state}${announcement.reason ? ` · ${reasons[announcement.reason] || announcement.reason.replace(/_/g, ' ')}` : ''}`;
}

/** The config activity tab reads the same durable receipts shown beside jobs. */
export function PackInstallationActivity({ serverAddress }: { serverAddress: string }) {
  const [history, setHistory] = useState<UpdaterInstallationRecord[] | null | undefined>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setHistory(null);
    void modpackUpdaterApi.server(serverAddress, controller.signal).then(result => {
      if (!controller.signal.aborted) { setHistory(result.installation_history); setError(null); }
    }).catch(failure => {
      if (!controller.signal.aborted) { setHistory(undefined); setError(failure instanceof Error ? failure.message : 'Installation history could not be loaded.'); }
    });
    return () => controller.abort();
  }, [serverAddress, reload]);
  return <div className="space-y-3">
    <div className="flex justify-end"><button type="button" className="glass-button-sm" onClick={() => setReload(value => value + 1)}><RefreshCw className="w-4 h-4" />Refresh installation history</button></div>
    {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    <PackInstallationHistory history={history} />
  </div>;
}
